/**
 * Pieces a loading screen is built from, so it can take the shape of the
 * screen it stands in for.
 *
 * Anything a screen knows before its data lands (headings, tab labels, panel
 * titles) is rendered for real; a bone stands in only where data will go.
 *
 * @module components/ui/SkeletonParts
 */

import React from "react";

/** One placeholder shape, sized by its classes. Decorative. */
export function Bone({ className }: { className: string }): React.ReactElement {
  return <span aria-hidden="true" className={`skeleton block ${className}`} />;
}

/** A labelled form control. */
export function SkeletonField({ className = "" }: { className?: string }): React.ReactElement {
  return (
    <div aria-hidden="true" className={`flex flex-col gap-2 ${className}`.trim()}>
      <Bone className="h-4 w-28" />
      <Bone className="h-10 w-full" />
    </div>
  );
}

/** A paragraph: full lines and a short last one. */
export function SkeletonText({
  lines = 2,
  className = "",
}: {
  lines?: number;
  className?: string;
}): React.ReactElement {
  return (
    <div aria-hidden="true" className={`flex flex-col gap-2 ${className}`.trim()}>
      {Array.from({ length: lines }, (_, index) => (
        <Bone
          // Position is a placeholder line's only identity.
          key={`line-${String(index)}`}
          className={`h-4 ${index === lines - 1 ? "w-2/3" : "w-full"}`}
        />
      ))}
    </div>
  );
}

/** Rows of a table under its header row. */
export function SkeletonTable({
  columns,
  rows,
}: {
  columns: number;
  rows: number;
}): React.ReactElement {
  const row = (key: string, height: string) => (
    <div key={key} className="flex gap-4 border-b border-base-300 px-3 py-3">
      {Array.from({ length: columns }, (_, index) => (
        <span key={`cell-${String(index)}`} className={index === 0 ? "flex-[2]" : "flex-1"}>
          <Bone className={`${height} ${index === 0 ? "w-3/4" : "w-1/2"}`} />
        </span>
      ))}
    </div>
  );
  return (
    <div aria-hidden="true" className="flex flex-col">
      {row("head", "h-3")}
      {Array.from({ length: rows }, (_, index) => row(`row-${String(index)}`, "h-4"))}
    </div>
  );
}

/** A chart's plot area, as bars. */
export function ChartBones(): React.ReactElement {
  return (
    <div aria-hidden="true" className="flex h-64 items-end gap-3">
      {["h-1/3", "h-2/3", "h-1/2", "h-full", "h-1/4", "h-3/5"].map((height) => (
        <Bone key={height} className={`${height} flex-1`} />
      ))}
    </div>
  );
}

/**
 * Names a wait when the bones are the whole region: by name, as `Skeleton`
 * does, and as text, as `LoadingState` does, so either kind of reader finds it.
 */
function statusProps(label: string | undefined): React.HTMLAttributes<HTMLDivElement> {
  return label ? { role: "status", "aria-label": label } : { "aria-hidden": true };
}

function StatusText({ label }: { label: string | undefined }): React.ReactElement | null {
  return label ? <span className="sr-only">{label}</span> : null;
}

/**
 * Rows of a list: a title over a line of detail. `boxed` draws each as its
 * own card, as a queue of work items is.
 */
export function ListBones({
  rows,
  boxed = false,
  label = undefined,
}: {
  rows: number;
  boxed?: boolean;
  label?: string;
}): React.ReactElement {
  return (
    <div {...statusProps(label)} className={`flex flex-col ${boxed ? "gap-3" : ""}`.trim()}>
      <StatusText label={label} />
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={`row-${String(index)}`}
          aria-hidden="true"
          className={
            boxed
              ? "flex flex-col gap-2 rounded-box border border-base-300 p-4"
              : "flex flex-col gap-2 border-b border-base-300 py-3 last:border-b-0"
          }
        >
          <Bone className="h-4 w-2/3" />
          {boxed ? <Bone className="h-5 w-48" /> : null}
          <Bone className="h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

/** Labelled progress bars: a name and its count over the bar. */
export function BarBones({
  rows,
  label = undefined,
}: {
  rows: number;
  label?: string;
}): React.ReactElement {
  return (
    <div {...statusProps(label)} className="flex flex-col gap-3">
      <StatusText label={label} />
      {Array.from({ length: rows }, (_, index) => (
        <div key={`bar-${String(index)}`} aria-hidden="true" className="flex flex-col gap-1">
          <div className="flex justify-between gap-3">
            <Bone className="h-4 w-32" />
            <Bone className="h-4 w-12" />
          </div>
          <Bone className="h-2 w-full" />
        </div>
      ))}
    </div>
  );
}

/** A form section's panel: its heading, a lead line, and fields two abreast. */
export function FormPanelBones({ label = undefined }: { label?: string }): React.ReactElement {
  return (
    <div {...statusProps(label)} className="rounded-box border border-base-300 p-6 max-sm:p-4">
      <StatusText label={label} />
      <div aria-hidden="true" className="flex flex-col gap-5">
        <Bone className="h-5 w-48" />
        <SkeletonText lines={1} />
        <div className="grid gap-4 sm:grid-cols-2">
          <SkeletonField />
          <SkeletonField />
          <SkeletonField />
          <SkeletonField />
        </div>
      </div>
    </div>
  );
}

/** A set of choice cards, two abreast. */
export function ChoiceBones({
  count,
  label = undefined,
}: {
  count: number;
  label?: string;
}): React.ReactElement {
  return (
    <div {...statusProps(label)} className="grid gap-3 sm:grid-cols-2">
      <StatusText label={label} />
      {Array.from({ length: count }, (_, index) => (
        <Bone key={`choice-${String(index)}`} className="h-16 w-full" />
      ))}
    </div>
  );
}

/** A collection's cards, in the grid its loaded cards take. */
export function CardBones({
  count,
  label = undefined,
}: {
  count: number;
  label?: string;
}): React.ReactElement {
  return (
    <div {...statusProps(label)} className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      <StatusText label={label} />
      {Array.from({ length: count }, (_, index) => (
        <div
          key={`card-${String(index)}`}
          aria-hidden="true"
          className="flex flex-col gap-3 rounded-box border border-base-300 p-4"
        >
          <Bone className="h-5 w-2/3" />
          <Bone className="h-4 w-full" />
          <Bone className="h-4 w-1/2" />
        </div>
      ))}
    </div>
  );
}
